// Diff engine des collections synchronisées entre co-éditeurs.
//
// Produit pour chaque changement d'état un diff `forward` (à diffuser via
// patch_structure / à persister) et son `inverse` (future pile undo locale).
// Pur et indépendant du réseau : la sérialisation DraftJS (convertToRaw /
// transformTextToEditorState) se fait aux frontières, pas ici — les entités
// sont comparées par référence (garanti par le structural sharing d'immer).
//
// Format d'un diff :
// {
//   upserts: { boxes: [entity...], variables: { key: value } },
//   deletes: { boxes: [id...],     variables: [key...] },
//   order:   { containers: [id...] },          // séquence complète ordonnée
//   values:  { configuration: {...} },         // collections remplacées en bloc
// }

import { byId } from '../store/lookups';

// Liste blanche : tout le reste du store (sélection, recherche, slider...)
// est de l'état local par utilisateur, jamais diffusé.
export const ENTITY_ARRAYS = [
  'boxes',
  'containers',
  'columns',
  'rowContainers',
  'variableGroups',
  'masks',
];
export const KEYED_OBJECTS = ['variables', 'triggers'];
export const WHOLE_VALUES = [
  'configuration',
  'variableOrderArray',
  'summaryConfiguration',
  'gridConfiguration',
];
// Seuls les containers ont un ordre signifiant (empilement vertical).
export const ORDERED_ARRAYS = ['containers'];

// Entités issues d'une section LIÉE. Elles sont dérivées : ré-injectées
// localement à chaque chargement et strippées à la sauvegarde
// (requests/templates.js pour les entités, dataSync.js#triggersToRaw pour les
// triggers). Leurs ids de container/colonne sont des uuid aléatoires qui
// divergent d'un client à l'autre → on ne les diffuse ni ne les persiste
// JAMAIS, sinon le voisin reçoit des entités orphelines (boxes vides).
// Cohérent avec le save : dérivé = jamais persisté = jamais diffusé. La ref
// d'origine est préservée quand il n'y a aucune section liée, pour ne pas
// casser le cache byId (structural sharing).
//
// Arrays d'entités (boxes/containers/columns) : l'entité entière porte
// `from_linked_section`.
const LINKED_SECTION_ENTITY_KEYS = new Set([
  'boxes',
  'containers',
  'columns',
  'rowContainers',
]);
export const stripLinkedSectionEntities = (key, arr) => {
  if (!LINKED_SECTION_ENTITY_KEYS.has(key)) return arr;
  return arr.some((e) => e.from_linked_section)
    ? arr.filter((e) => !e.from_linked_section)
    : arr;
};

// Triggers : objet `{ boxId: [trigger...] }`. Chaque trigger de section liée
// porte `linked_section` (miroir de `from_linked_section` des boxes, cf.
// utils/triggers.js) — même critère que le save (dataSync.js#triggersToRaw). On
// retire ces triggers et on drop les clés vidées.
export const stripLinkedSectionTriggers = (obj) => {
  let touched = false;
  const out = {};
  Object.keys(obj).forEach((key) => {
    const arr = obj[key];
    if (Array.isArray(arr) && arr.some((t) => t.linked_section)) {
      touched = true;
      const kept = arr.filter((t) => !t.linked_section);
      if (kept.length) out[key] = kept;
    } else {
      out[key] = arr;
    }
  });
  return touched ? out : obj;
};

const sameIdSequence = (prev, next) =>
  prev.length === next.length && prev.every((e, i) => e.id === next[i].id);

const diffEntityArray = (key, prevRaw, nextRaw, forward, inverse) => {
  if (prevRaw === nextRaw) return false;
  const prev = stripLinkedSectionEntities(key, prevRaw);
  const next = stripLinkedSectionEntities(key, nextRaw);
  let changed = false;

  // Maps id → entité cachées par référence de tableau (cf. lookups.js) :
  // souvent déjà construites par les renders, et réutilisées par eux ensuite.
  const prevById = byId(prev);
  const nextById = byId(next);
  const forwardUpserts = [];
  const forwardDeletes = [];
  const inverseUpserts = [];
  const inverseDeletes = [];

  // Un store qui contient deux entités de même id ne doit pas produire deux
  // upserts du même id : le backend le refuse (CardinalityViolation).
  const seenIds = new Set();
  next.forEach((entity) => {
    if (seenIds.has(entity.id)) return;
    seenIds.add(entity.id);
    const prevEntity = prevById.get(entity.id);
    if (prevEntity === undefined) {
      forwardUpserts.push(entity);
      inverseDeletes.push(entity.id);
    } else if (prevEntity !== entity) {
      forwardUpserts.push(entity);
      inverseUpserts.push(prevEntity);
    }
  });
  prev.forEach((entity) => {
    if (!nextById.has(entity.id)) {
      forwardDeletes.push(entity.id);
      inverseUpserts.push(entity);
    }
  });

  if (forwardUpserts.length) {
    forward.upserts[key] = forwardUpserts;
    changed = true;
  }
  if (forwardDeletes.length) {
    forward.deletes[key] = forwardDeletes;
    changed = true;
  }
  if (inverseUpserts.length) inverse.upserts[key] = inverseUpserts;
  if (inverseDeletes.length) inverse.deletes[key] = inverseDeletes;

  if (ORDERED_ARRAYS.includes(key) && !sameIdSequence(prev, next)) {
    forward.order[key] = next.map((e) => e.id);
    inverse.order[key] = prev.map((e) => e.id);
    changed = true;
  }

  return changed;
};

const diffKeyedObject = (key, prevRaw, nextRaw, forward, inverse) => {
  if (prevRaw === nextRaw) return false;
  const prev = key === 'triggers' ? stripLinkedSectionTriggers(prevRaw) : prevRaw;
  const next = key === 'triggers' ? stripLinkedSectionTriggers(nextRaw) : nextRaw;
  let changed = false;

  const forwardUpserts = {};
  const forwardDeletes = [];
  const inverseUpserts = {};
  const inverseDeletes = [];

  Object.keys(next).forEach((k) => {
    if (!(k in prev)) {
      forwardUpserts[k] = next[k];
      inverseDeletes.push(k);
    } else if (prev[k] !== next[k]) {
      forwardUpserts[k] = next[k];
      inverseUpserts[k] = prev[k];
    }
  });
  Object.keys(prev).forEach((k) => {
    if (!(k in next)) {
      forwardDeletes.push(k);
      inverseUpserts[k] = prev[k];
    }
  });

  if (Object.keys(forwardUpserts).length) {
    forward.upserts[key] = forwardUpserts;
    changed = true;
  }
  if (forwardDeletes.length) {
    forward.deletes[key] = forwardDeletes;
    changed = true;
  }
  if (Object.keys(inverseUpserts).length) inverse.upserts[key] = inverseUpserts;
  if (inverseDeletes.length) inverse.deletes[key] = inverseDeletes;

  return changed;
};

// L'ordre du tableau `boxes` est signifiant côté rendu (le bloc sommaire lit
// les titres en ordre de tableau) et côté déplacement de titres (splices par
// index dans utils/titles.js). Les upserts de nouvelles boxes arrivant en fin
// de tableau, on réaligne sur l'ordre visuel du document : containers →
// columns. Tri stable — l'ordre relatif intra-colonne est préservé, les boxes
// sans colonne connue restent en fin (même convention que
// getBoxesInDocumentOrder dans utils/titles.js).
const sortBoxesByDocumentOrder = (boxes, containers) => {
  const rankByColumnId = new Map();
  containers.forEach((container) => {
    (container.columnsIds ?? []).forEach((columnId) => {
      if (!rankByColumnId.has(columnId)) {
        rankByColumnId.set(columnId, rankByColumnId.size);
      }
    });
  });
  const unknownRank = rankByColumnId.size;
  return [...boxes].sort(
    (a, b) =>
      (rankByColumnId.get(a.columnId) ?? unknownRank) -
      (rankByColumnId.get(b.columnId) ?? unknownRank)
  );
};

const emptyDiff = () => ({ upserts: {}, deletes: {}, order: {}, values: {} });

// Compare deux états du store sur les collections synchronisées.
// Retourne { forward, inverse } ou null si rien n'a changé.
// L'ordre des entités n'est garanti que pour ORDERED_ARRAYS (containers) ;
// pour les autres, seul le contenu par id est significatif.
export const computeDiff = (prevState, nextState) => {
  const forward = emptyDiff();
  const inverse = emptyDiff();
  let changed = false;

  ENTITY_ARRAYS.forEach((key) => {
    if (
      diffEntityArray(
        key,
        prevState[key] ?? [],
        nextState[key] ?? [],
        forward,
        inverse
      )
    ) {
      changed = true;
    }
  });

  KEYED_OBJECTS.forEach((key) => {
    if (
      diffKeyedObject(
        key,
        prevState[key] ?? {},
        nextState[key] ?? {},
        forward,
        inverse
      )
    ) {
      changed = true;
    }
  });

  WHOLE_VALUES.forEach((key) => {
    if (prevState[key] !== nextState[key]) {
      forward.values[key] = nextState[key];
      inverse.values[key] = prevState[key];
      changed = true;
    }
  });

  return changed ? { forward, inverse } : null;
};

// Applique un diff à un état et retourne un état partiel (seulement les
// collections modifiées, avec de nouvelles références — compatible zustand set).
// Ne mute jamais `state`.
export const applyDiff = (state, diff) => {
  const partial = {};
  if (!diff) return partial;
  let boxesAppended = false;
  let appendedBoxes = new Set();

  ENTITY_ARRAYS.forEach((key) => {
    const upserts = diff.upserts?.[key];
    const deletes = diff.deletes?.[key];
    const order = diff.order?.[key];
    if (!upserts && !deletes && !order) return;

    let entities = state[key] ?? [];

    if (upserts || deletes) {
      const deleteIds = new Set(deletes ?? []);
      const upsertById = new Map((upserts ?? []).map((e) => [e.id, e]));
      entities = entities
        .filter((e) => !deleteIds.has(e.id))
        .map((e) => {
          if (upsertById.has(e.id)) {
            const updated = upsertById.get(e.id);
            upsertById.delete(e.id);
            return updated;
          }
          return e;
        });
      // Entités nouvelles : ajoutées en fin (l'ordre vient de `order` si signifiant)
      const appended = [...upsertById.values()];
      entities = [...entities, ...appended];
      if (key === 'boxes' && appended.length) {
        boxesAppended = true;
        appendedBoxes = new Set(appended);
      }
    }

    if (order) {
      const position = new Map(order.map((id, index) => [id, index]));
      // Les entités hors `order` — sections liées locales (jamais diffusées,
      // donc absentes de tout order distant) ou course rare — restent ancrées
      // juste après leur prédécesseur ordonné, au lieu d'être poussées en fin :
      // sinon les sections liées du récepteur sauteraient en bas du document à
      // chaque réordonnancement distant. `tie` préserve leur ordre local.
      let lastRank = -1;
      let tie = 0;
      entities = entities
        .map((entity) => {
          if (position.has(entity.id)) {
            lastRank = position.get(entity.id);
            tie = 0;
            return [entity, lastRank, 0];
          }
          tie += 1;
          return [entity, lastRank, tie];
        })
        .sort((a, b) => a[1] - b[1] || a[2] - b[2])
        .map(([entity]) => entity);
    }

    partial[key] = entities;
  });

  // Une box AJOUTÉE dont la colonne n'existe pas dans les colonnes résultantes
  // (supprimée par un co-éditeur, ou par ce diff) serait orpheline : invisible
  // dans la page mais listée au sommaire. Même prédicat que isOrphanBox
  // (utils/boxes.js, non importé pour garder ce module pur) : pas de columnId
  // ou colonnes inconnues → conservée. Les mises à jour ne sont pas filtrées.
  const resultingColumns = partial.columns ?? state.columns;
  if (boxesAppended && Array.isArray(resultingColumns)) {
    const columnIds = new Set(resultingColumns.map((c) => c.id));
    partial.boxes = partial.boxes.filter(
      (b) => !appendedBoxes.has(b) || !b.columnId || columnIds.has(b.columnId)
    );
  }

  // Nouvelles boxes distantes : réalignement sur l'ordre du document, avec les
  // containers du même diff déjà appliqués (ENTITY_ARRAYS traite boxes avant
  // containers, donc partial.containers est disponible ici).
  if (boxesAppended) {
    partial.boxes = sortBoxesByDocumentOrder(
      partial.boxes,
      partial.containers ?? state.containers ?? []
    );
  }

  KEYED_OBJECTS.forEach((key) => {
    const upserts = diff.upserts?.[key];
    const deletes = diff.deletes?.[key];
    if (!upserts && !deletes) return;

    const nextObject = { ...(state[key] ?? {}) };
    (deletes ?? []).forEach((k) => delete nextObject[k]);
    Object.assign(nextObject, upserts ?? {});
    partial[key] = nextObject;
  });

  Object.entries(diff.values ?? {}).forEach(([key, value]) => {
    partial[key] = value;
  });

  return partial;
};
